import {
  IPublicClientApplication,
  AccountInfo,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';

/**
 * Thrown when MSAL initiates a redirect for interactive authentication.
 * The current request will not complete — the page is navigating away.
 */
export class AuthRedirectError extends Error {
  constructor() {
    super('Authentication redirect initiated. The page will reload.');
    this.name = 'AuthRedirectError';
  }
}

export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  [key: string]: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly statusText: string;
  readonly method: string;
  readonly url: string;
  readonly body?: string;
  readonly problemDetails?: ProblemDetails;

  constructor(init: {
    status: number;
    statusText: string;
    method: string;
    url: string;
    body?: string;
    problemDetails?: ProblemDetails;
  }) {
    super(`API ${init.method} ${init.url} returned ${init.status} ${init.statusText}`.trim());
    this.name = 'ApiError';
    this.status = init.status;
    this.statusText = init.statusText;
    this.method = init.method;
    this.url = init.url;
    this.body = init.body;
    this.problemDetails = init.problemDetails;
  }
}

export interface ApiRequestOptions {
  signal?: AbortSignal;
  /**
   * Extra request headers merged on top of the defaults (Authorization,
   * Content-Type). Used for conditional requests such as `If-Match` /
   * `If-None-Match` in optimistic-concurrency flows.
   */
  headers?: Record<string, string>;
}

export class ApiService {
  private static readonly BASE_PATH = `${import.meta.env.VITE_API_URL ?? ''}/api`;

  private instance: IPublicClientApplication;
  private account: AccountInfo;
  private scope: string;

  constructor(instance: IPublicClientApplication, account: AccountInfo) {
    const scope = import.meta.env.VITE_API_SCOPE;
    if (!scope) {
      throw new Error(
        'VITE_API_SCOPE environment variable is not configured. Cannot make authenticated API requests.'
      );
    }

    this.instance = instance;
    this.account = account;
    this.scope = scope;
  }

  private async getAccessToken(): Promise<string> {
    try {
      const tokenResponse = await this.instance.acquireTokenSilent({
        scopes: [this.scope],
        account: this.account,
      });
      return tokenResponse.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Redirect is terminal — the page navigates away
        await this.instance.acquireTokenRedirect({
          scopes: [this.scope],
          account: this.account,
        });
        throw new AuthRedirectError();
      }
      throw error;
    }
  }

  private async request<T>(
    url: string,
    method: string,
    body?: unknown,
    options?: ApiRequestOptions
  ): Promise<T> {
    const resolvedUrl = url.startsWith('http') ? url : `${ApiService.BASE_PATH}${url}`;
    const accessToken = await this.getAccessToken();

    const headers: Record<string, string> = {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
      ...(options?.headers ?? {}),
    };

    const fetchOptions: RequestInit = {
      method,
      headers,
      signal: options?.signal,
    };

    if (body !== undefined) {
      fetchOptions.body = JSON.stringify(body);
    }

    const response = await fetch(resolvedUrl, fetchOptions);

    if (!response.ok) {
      let body: string | undefined;
      let problemDetails: ProblemDetails | undefined;
      try {
        body = await response.text();
      } catch {
        // Ignore: body was not readable.
      }
      if (body) {
        const contentType = response.headers.get('content-type') ?? '';
        if (contentType.includes('json') || contentType.includes('problem+json')) {
          try {
            const parsed = JSON.parse(body) as unknown;
            if (parsed && typeof parsed === 'object') {
              problemDetails = parsed as ProblemDetails;
            }
          } catch {
            // Body claimed JSON but wasn't parseable; keep raw body only.
          }
        }
      }
      throw new ApiError({
        status: response.status,
        statusText: response.statusText,
        method,
        url,
        body,
        problemDetails,
      });
    }

    const contentType = response.headers.get('content-type') ?? '';
    const contentLength = response.headers.get('content-length');
    if (response.status === 204 || contentLength === '0') {
      return undefined as T;
    }
    if (contentType.includes('json')) {
      return response.json() as Promise<T>;
    }
    // Fallback: surface the raw body as text so callers consuming non-JSON endpoints (text/plain, CSV, etc.) still receive it.
    // Binary payloads should add a dedicated path here (e.g. blob()).
    return response.text() as Promise<T>;
  }

  async get<T>(url: string, options?: ApiRequestOptions): Promise<T> {
    return this.request<T>(url, 'GET', undefined, options);
  }

  async put<T>(url: string, body: unknown, options?: ApiRequestOptions): Promise<T> {
    return this.request<T>(url, 'PUT', body, options);
  }

  async post<T>(url: string, body: unknown, options?: ApiRequestOptions): Promise<T> {
    return this.request<T>(url, 'POST', body, options);
  }

  async delete<T>(url: string, options?: ApiRequestOptions): Promise<T> {
    return this.request<T>(url, 'DELETE', undefined, options);
  }
}
